/**
 * v0.4 章② 条件集合・階層継承カタログ の日本語ラベル静的辞書。
 * ⚠️ 章③でラベル辞書 (label_key_catalog) が実装されるまでの暫定措置。
 * setting_key / attribute_type_id / usage_point_id はプラットフォーム提供カタログのため
 * 種類が変わることは稀だが、追加時はここも同時に更新すること。
 */

export const ATTRIBUTE_TYPE_LABELS: Record<string, string> = {
  employment_type: '雇用形態',
  employee_category: '社員区分',
  position_level: '役職・等級',
  skill: 'スキル',
  role_profile: '人材像',
  node_assignment: '所属ノード',
  employee_flag: '個人フラグ',
  relation_to_viewer: '閲覧者との関係',
  // 2026-09-18: 社員に紐付いていない閲覧者 (会社管理者等) にも対象者との関係に依らず可視性を
  // 開放できるようにする条件型 (docs/known-pitfalls.md §8-199 の実運用要望)。
  viewer_permission: '閲覧者の権限',
  // 2026-09-19: 社員番号・氏名で対象者を直接指定する条件型 (docs/design/
  // v05-condition-employee-identity-attribute-detail.md)。既存9種は全て「カテゴリ」による絞り込みだが、
  // これは唯一「特定の個人」を名指しで指定する属性型。
  employee_identity: '特定の社員',
  // 月次実績 I-5a (2026-09-27、migration 0187): grade は position_level と同型 (人単位、§3-2)。
  grade: 'グレード',
  // target_node_relation: 承認者の勤務所属が基準店舗と同一/配下かを表す (work_record_approver 専用)。
  target_node_relation: '基準店舗との関係',
};

export const OPERATOR_LABELS: Record<string, string> = {
  in: 'のいずれかに該当',
  not_in: 'のいずれにも該当しない',
  eq: 'に該当',
  gte: '以上',
  lte: '以下',
  // v0.4 追加要件⑬ (D-402): skill 属性専用。習熟度を問わず保有していれば true。
  has: '保有している',
};

/** relation_to_viewer の選択肢 (D-99/D-118、システム提供の有限集合)。 */
export const RELATION_OPTIONS: { id: string; label: string }[] = [
  { id: 'same_node', label: '閲覧者と同じノードに所属' },
  { id: 'descendant_node', label: '閲覧者の配下ノードに所属' },
  { id: 'other_node', label: '上記以外' },
  { id: 'position_higher', label: '閲覧者より役職が上' },
  { id: 'position_equal', label: '閲覧者と役職が同じ' },
  { id: 'position_lower', label: '閲覧者より役職が下' },
  { id: 'same_team', label: '閲覧者と同じチームに所属' },
  { id: 'direct_report', label: '閲覧者の直属の部下' },
  { id: 'shares_role_profile', label: '閲覧者と共通の人材像を持つ' },
];

/** employee_flag の選択肢 (システム提供の有限集合)。現状は掛け持ちフラグ (D-63、
 *  employee_income_caps.has_multiple_employers) の1種類のみ。他ページの表記
 *  (EmployeeDetailPage.tsx の「他社と掛け持ちしている」) に合わせる。 */
export const EMPLOYEE_FLAG_OPTIONS: { id: string; label: string }[] = [
  { id: 'has_multiple_employers', label: '他社と掛け持ちしている' },
];

/** target_node_relation の選択肢 (月次実績 I-5a、work_record_approver 専用、システム提供の有限集合)。 */
export const TARGET_NODE_RELATION_OPTIONS: { id: string; label: string }[] = [
  { id: 'same_node', label: '基準店舗そのものに所属' },
  { id: 'same_or_descendant', label: '基準店舗と同じ、または基準店舗より上位の階層に所属' },
];

/** viewer_permission の action 選択肢 (2026-09-18)。CheckableAction (lib/scope.ts) 全体ではなく、
 *  この条件型が実際に許可される利用箇所 (現状 skill_visibility のみ) が持つ2アクションに絞る
 *  (§6d: 今すぐ全部を選べるようにする必要はない、需要が出たら拡張する)。 */
export const VIEWER_PERMISSION_ACTION_OPTIONS: { id: string; label: string }[] = [
  { id: 'view', label: '閲覧' },
  { id: 'manage', label: '管理' },
];

export const USAGE_POINT_LABELS: Record<string, string> = {
  staffing_constraint_subject: '必要人員の対象者条件',
  visibility_rule: '可視性ルール (金額・情報の表示範囲)',
  standard_rate_tier: '標準単価の区分',
  assignment_stage_subject: 'シフト割当ステージの対象者条件',
  allowance_eligibility: '手当の対象者条件',
  urgent_request_candidate: '急な出勤依頼の候補者条件',
  // ⚠️⚠️ §8-152: 以下5件は migration 0084/0086/0087/0093 で追加されていたのに
  //  この辞書へ同期されておらず、`/settings/conditions` に**英語の生 ID が表示されていた**
  //  (ConditionsSettingsPage.tsx が `USAGE_POINT_LABELS[id] ?? id` で生値へフォールバックする)。
  //  USAGE_POINT_IDS は型、USAGE_POINT_EVALUATORS は catalog-consistency.test.ts C-2 に
  //  守られていて漏れていない — **検出器が無いこの辞書だけが5回連続で漏れた**。
  //  恒久対策 (USAGE_POINT_IDS との集合一致の機械検証) は Q-304。
  leave_visibility: '休暇情報の表示範囲',
  family_info_visibility: '家族情報の表示範囲',
  approval_recipient: '承認者・共有先の条件',
  request_type_eligibility: '申請種別の利用資格',
  request_field_visibility: '申請項目の表示範囲',
  // v0.4 追加要件⑰ (migration 0118)
  document_visibility: '社内文書の公開範囲',
  // v0.4 追加要件⑳-3 (migration 0124、D-583)
  work_record_approver: '勤務実績の承認者条件',
  // v0.4 使い勝手フィードバック⑤ (D-U7、migration 0128)
  skill_visibility: 'スキル・人材像・役職情報の表示範囲',
  // 使い勝手フィードバック⑦ (D-U38、migration 0134)
  request_type_proxy_requester: '代理申請者の条件',
};

export const MATCH_MODE_LABELS = {
  all: 'すべてに一致 (AND)',
  any: 'いずれかに一致 (OR)',
} as const;

/** 階層継承カタログ (inheritable_settings) の設定キー → 日本語ラベル。 */
export const SETTING_LABELS: Record<string, string> = {
  staffing_requirement: '必要人員の制約',
  employee_skill: '個人スキルの上書き',
  shift_pattern: 'シフトパターン (勤務区分の時刻)',
  business_hours: '営業時間',
  agreement_36: '36協定',
  attendance_setting: '勤怠設定 (割増率・法定休日等)',
  shift_period_cycle: 'シフト期間の周期',
  shift_draft_deadline: '店舗案の提出締切',
  unsubmitted_store_handling: '未提出店舗の扱い',
  support_scope_level: '応援を依頼できる範囲',
  shift_notice_timing: 'シフト確定通知のタイミング',
  shift_notice_delivery: 'シフト確定通知の配信方法 (push/pull)',
  delivery_failure_mode: '通知配信失敗時の挙動',
  agreement_36_expiry_notice: '36協定 失効前通知',
  labor_cost_granularity: '人件費速報の粒度 (店長への開示範囲)',
  wage_basis: '人件費計算の単価基準 (標準単価/実額)',
  time_display_mode: '時刻表示形式',
  sensitive_view_audit_mode: '機微情報閲覧の監査記録',
  standing_pattern_review: '恒常パターンの見直しレビュー',
  labor_cost_allocation: '応援時の人件費計上先',
  post_confirm_approval_reduce: '確定後のシフト削減 承認要否',
  post_confirm_approval_increase: '確定後のシフト増員 承認要否',
  post_confirm_approval_swap: '確定後のシフト交代 承認要否',
  weekly_contracted_work_days: '週所定労働日数の標準値',
  weekly_contracted_minutes: '週所定労働時間の標準値 (分)',
  default_work_pattern_horizon_months: '既定勤務パターンの生成見通し期間 (ヶ月)',
  // ⚠️⚠️ §8-152 の恒久対策テスト (web-static-dictionaries.test.ts) が検出した未登録11件。
  //  `InheritanceSettingsPage.tsx:215,260` は `SETTING_LABELS[key] ?? key` で**生の英字 ID へ
  //  フォールバックする**ため、階層継承の設定画面に英語のキーがそのまま出ていた。
  //  ラベルは `ai_setup_topics` の日本語 (migration 0073/0085) に揃え、無いものは設計書の用語に合わせた。
  visitor_count_config: '来客数の集計方法',
  approval_workflow_binding: '承認ワークフローの割当',
  shift_preference_input_mode: 'シフト希望の入力方法',
  shift_past_edit_grace_days: '過去シフト編集の猶予日数',
  shift_period_max_future_days: 'シフト作成可能な先行日数',
  shift_draft_skip_closed_days: '定休日を自動生成から除外するか',
  travel_buffer_minutes: '店舗間移動の標準所要時間 (分)',
  annual_leave_grant_basis: '有休の付与基準日',
  annual_leave_hourly_enabled: '時間単位年休の採用可否',
  leave_five_day_obligation_alert_days: '年5日取得義務のアラート時期',
  assignment_stage_set: 'シフト割当ステージの構成',
  shift_print_display_mode: 'シフト印刷時の表示形式 (パターン名/時刻)',
  // 人・組織機能 増分② (migration 0139): resolveConfirmationProcess が消費する
  certification_confirmation_process: '認定の確定プロセス (店長認定+オーナー確定/目線合わせ会議)',
  // 人・組織機能 増分③ (migration 0140): 配置制約。⚠️ assumed_end_time/node_has_parking/
  // node_has_bicycle_parkingはjudgePlacementが未消費のためSCALAR_SETTING_KEYSから除外済み
  // (D-674、design-and-code/platform-reviewerレビューCRITICAL)。実際に消費する増分で追加すること。
  placement_dimensions_enabled: '利用する制限次元 (時間/場所/業務)',
  commute_types_enabled: '通勤種別の選択肢',
  placement_review_cycle_months: '恒久制約の棚卸し周期 (月数)',
  // 人・組織機能 増分⑦ (migration 0143): resolveDevelopmentPlanSelfOriginEnabled/
  // resolveDevelopmentPlanDefaultMonths (lib/people-org/development-plan.ts) が消費する。
  development_plan_self_origin_enabled: '本人希望による育成計画の作成を許可するか',
  development_plan_default_months: '育成計画の期限の既定値 (開始日からの月数)',
  // v0.5① 日報機能 増分1 step 5a (GET /daily-reports/form が消費する4件)。
  daily_report_default_target: '日報を開いたときの既定の営業日',
  daily_report_carryover_max_days: '前日値を遡って引き継ぐ上限日数',
  daily_report_unsubmitted_lookback_days: '未提出の日報を遡って検出する上限日数',
  daily_report_past_edit_grace_days: '過去日の日報を編集可能とする猶予日数',
  daily_report_ocr_gate_mode: 'OCR未確認時の提出の扱い (off/warn/block)',
  // 月次実績 第2フェーズ I-5 (§7-1/§3-6、migration 0187、policy_locked)。
  work_record_approval_basis: '勤務実績 承認の基準店舗',
  work_record_amend_on_approve: '勤務実績 「修正して承認」の許可',
  work_record_post_approval_edit_mode: '勤務実績 承認済みの修正方式',
  // 月次実績 第2フェーズ I-7 (migration 0191)
  daily_report_work_section_visible: '日報「勤務実績」欄の表示設定',
};

export const OVERRIDE_MODE_LABELS: Record<string, string> = {
  inherit_only: '継承のみ (下位では上書き不可)',
  override: '自由に上書き可能',
  constrained: '制約付きで上書き可能',
};

export const DIRECTION_CONSTRAINT_LABELS: Record<string, string> = {
  none: '制約なし',
  gte_parent: '親の値以上のみ許可',
  lte_parent: '親の値以下のみ許可',
};

export const OVERRIDE_PERSISTENCE_LABELS: Record<string, string> = {
  once: '一度きり',
  persistent: '明示解除まで有効',
  until_reassignment: '対象者の異動まで有効',
};
